import React, { useState } from 'react';
import invariant from 'tiny-invariant';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MoAlert } from '@src/components/MoAlert';
import { MoModal } from '@src/components/MoModal';
import { MoButton } from '@src/components/MoButton';
import { MoSpacing } from '@src/components/MoSpacing';
import { useSuccessFailureNotification } from '@src/hooks/useSuccessFailureNotification';
import { useChatsContext } from '../ChatsContext';
import { pluralize } from '@src/utils/stringUtils';
import { filterAvailableGroupChatParticipants } from '../utils';
import ChatParticipantEditor from './ChatParticipantEditor';

type ModalStep = 'edit' | 'confirm-new-chat';

type EditChatModalProps = {
  open: boolean;
  onClose: () => void;
};

type ModalActionsProps = {
  loading: boolean;
  submitLabel: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

function ModalActions({
  loading,
  submitLabel,
  cancelLabel = 'Cancel',
  submitDisabled,
  onCancel,
  onSubmit,
}: ModalActionsProps) {
  return (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
      <MoButton variant="outlined" onClick={onCancel} disabled={loading}>
        {cancelLabel}
      </MoButton>
      <LoadingButton
        variant="contained"
        onClick={onSubmit}
        loading={loading}
        disabled={submitDisabled}
      >
        {submitLabel}
      </LoadingButton>
    </Box>
  );
}

type ConfirmStepProps = {
  shouldCreateNewGroup: boolean;
  onShouldCreateNewGroupChange: (value: boolean) => void;
};

function ConfirmStep({ shouldCreateNewGroup, onShouldCreateNewGroupChange }: ConfirmStepProps) {
  return (
    <MoSpacing y={1}>
      <Typography>
        New supervisees can see the entire chat history. Would you like to create a new chat
        instead?
      </Typography>

      <RadioGroup
        value={shouldCreateNewGroup ? 'new' : 'existing'}
        onChange={(e) => onShouldCreateNewGroupChange(e.target.value === 'new')}
      >
        <FormControlLabel
          value="existing"
          control={<Radio />}
          label={
            <Box>
              <Typography fontWeight={500}>Add to existing chat</Typography>
            </Box>
          }
        />
        <FormControlLabel
          value="new"
          control={<Radio />}
          label={
            <Box>
              <Typography fontWeight={500}>Create new chat</Typography>
              <Typography variant="body2" color="text.secondary">
                Archive this chat and create a new chat with the participants you selected.
              </Typography>
            </Box>
          }
        />
      </RadioGroup>
    </MoSpacing>
  );
}

export default function EditChatModal({ open, onClose }: EditChatModalProps) {
  const { activeChatId, chat, chatRootPath, potentialChatParticipants, editChatConfig } =
    useChatsContext();
  const { callWithNotification } = useSuccessFailureNotification();

  invariant(
    chat?.activeNonViewerParticipants && activeChatId && editChatConfig,
    'EditChatModal should only render with a loaded chat and editChatConfig',
  );
  const { activeNonViewerParticipants } = chat;
  const { updateParticipants, updateParticipantsLoading, refetchChat } = editChatConfig;

  const [stagedParticipants, setStagedParticipants] = useState(activeNonViewerParticipants);
  const [step, setStep] = useState<ModalStep>('edit');
  const [shouldCreateNewGroup, setShouldCreateNewGroup] = useState(false);

  const loading = updateParticipantsLoading;

  const stagedIds = new Set(stagedParticipants.map((p) => p.id));
  const originalIds = new Set(activeNonViewerParticipants.map((p) => p.id));

  const availableParticipants = filterAvailableGroupChatParticipants({
    potentialParticipants: potentialChatParticipants,
    selectedParticipants: stagedParticipants,
  });
  const hasChanges =
    originalIds.size !== stagedIds.size ||
    [...originalIds].some((id) => !stagedIds.has(id)) ||
    [...stagedIds].some((id) => !originalIds.has(id));
  const newParticipantIds = [...stagedIds].filter((id) => !originalIds.has(id));
  const hasNewParticipants = newParticipantIds.length > 0;
  const hasRemovedParticipants = [...originalIds].some((id) => !stagedIds.has(id));

  const handleSubmit = async () => {
    if (!hasChanges) return;

    const successMessage = shouldCreateNewGroup ? 'New chat created' : 'Chat participants updated';

    await callWithNotification(
      async () => {
        const result = await updateParticipants({
          chatId: activeChatId,
          superviseeActorIds: stagedParticipants.map((p) => p.id),
          shouldCreateNewGroup,
        });

        if (!result.success) return false;

        if (result.chat?.id && result.chat.id !== activeChatId) {
          window.location.href = `${chatRootPath}/${result.chat.id}`;
        } else {
          refetchChat();
        }
        onClose();
        return true;
      },
      successMessage,
      'Failed to update participants',
    );
  };

  const handleSaveChangesClick = () => {
    if (hasNewParticipants) {
      setStep('confirm-new-chat');
    } else {
      handleSubmit();
    }
  };

  const handleBackToEdit = () => {
    setStep('edit');
    setShouldCreateNewGroup(false);
  };

  const isConfirmStep = step === 'confirm-new-chat';

  return (
    <MoModal
      open={open}
      onClose={onClose}
      title={
        isConfirmStep
          ? `How should the new ${pluralize('supervisee', newParticipantIds.length)} be added?`
          : 'Edit chat'
      }
      maxWidth="sm"
      actions={
        isConfirmStep ? (
          <ModalActions
            loading={loading}
            submitLabel="Save changes"
            cancelLabel="Back"
            onCancel={handleBackToEdit}
            onSubmit={handleSubmit}
          />
        ) : (
          hasChanges && (
            <ModalActions
              loading={loading}
              submitLabel="Save changes"
              submitDisabled={stagedParticipants.length === 0}
              onCancel={onClose}
              onSubmit={handleSaveChangesClick}
            />
          )
        )
      }
    >
      {isConfirmStep ? (
        <ConfirmStep
          shouldCreateNewGroup={shouldCreateNewGroup}
          onShouldCreateNewGroupChange={setShouldCreateNewGroup}
        />
      ) : (
        <Box>
          <ChatParticipantEditor
            selectedParticipants={stagedParticipants}
            availableParticipants={availableParticipants}
            disabled={loading}
            autocompletePlaceholder="Search supervisees"
            onRemove={(participantToRemove) =>
              setStagedParticipants((prev) =>
                prev.filter((participant) => participant.id !== participantToRemove.id),
              )
            }
            onAdd={(participantToAdd) =>
              setStagedParticipants((prev) => [...prev, participantToAdd])
            }
          />
          {hasRemovedParticipants && (
            <MoAlert severity="info">
              Removed supervisees can still see old messages but can’t send new ones.
            </MoAlert>
          )}
        </Box>
      )}
    </MoModal>
  );
}
